"use client";

import React, {
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Loader2,
  Lock,
  Mail,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  useRouter,
  useSearchParams,
} from 'next/navigation';
import toast from 'react-hot-toast';

import CloudflareTurnstile from '@/components/CloudflareTurnstile';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import config from '@/config';
import { getSafeAuthReturnPath } from '@/utils/auth-return-path';
import { getLocalizedPath } from '@/utils/utils';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';

export const runtime = 'edge';

interface LoginPageProps {
  params: { locale: string };
}

type TurnstileStatus = 'ready' | 'verified' | 'expired' | 'error' | 'timeout' | 'unsupported';

const getTurnstileStatusMessage = (status: TurnstileStatus, errorCode: string | undefined, locale: string) => {
  const isChineseLocale = locale.startsWith("zh");

  if (status === "expired") {
    return isChineseLocale ? "Cloudflare 验证已过期，请重新完成验证。" : "Cloudflare verification expired. Please complete it again.";
  }

  if (status === "timeout") {
    return isChineseLocale ? "Cloudflare 验证超时，请重新完成验证。" : "Cloudflare verification timed out. Please complete it again.";
  }

  if (status === "unsupported") {
    return isChineseLocale
      ? "当前浏览器不支持 Cloudflare 验证，请换用其他浏览器或关闭相关插件后重试。"
      : "This browser does not support Cloudflare verification. Try another browser or disable blocking extensions.";
  }

  if (status === "error") {
    return isChineseLocale
      ? `Cloudflare 验证失败，请刷新验证后重试${errorCode ? `（错误码：${errorCode}）` : ""}。`
      : `Cloudflare verification failed. Refresh the challenge and try again${errorCode ? ` (code: ${errorCode})` : ""}.`;
  }

  return null;
};

const getAuthErrorMessage = (message: string | undefined, code: string | undefined, locale: string) => {
  const normalizedMessage = (message || "").toLowerCase();
  const normalizedCode = (code || "").toLowerCase();
  const isChineseLocale = locale.startsWith("zh");

  if (normalizedCode === "captcha_failed" || normalizedMessage.includes("captcha") || normalizedMessage.includes("challenge")) {
    return isChineseLocale
      ? "Cloudflare 验证未通过，请重新完成验证后再登录。"
      : "Cloudflare verification failed. Please complete the challenge again before signing in.";
  }

  if (normalizedMessage.includes("invalid login credentials")) {
    return isChineseLocale
      ? "邮箱或密码不正确。如果你使用 Google 注册，请使用 Google 登录。"
      : "Email or password is incorrect. If you signed up with Google, use Google sign in.";
  }

  if (normalizedMessage.includes("email not confirmed")) {
    return isChineseLocale
      ? "请先打开邮箱中的确认链接，再回来登录。"
      : "Please confirm your email address before signing in.";
  }

  return message || (isChineseLocale ? "登录失败，请稍后重试。" : "Sign in failed. Please try again.");
};

export default function Login({ params: { locale } }: LoginPageProps) {
  const supabase = createClientComponentClient();
  const t = useTranslations("Auth");
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileMessage, setTurnstileMessage] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
  const isTurnstileConfigured = !!turnstileSiteKey;
  const intent = searchParams.get("intent") === "faceless-video"
    ? "faceless-video"
    : null;
  const requestedNext = searchParams.get("next");
  const safeNext = getSafeAuthReturnPath(requestedNext, "");
  const rawSource = searchParams.get("source") || "direct";
  const source = /^[a-z0-9-]{1,80}$/i.test(rawSource) ? rawSource : "direct";
  const destinationPath = safeNext
    || (intent === "faceless-video"
      ? `${getLocalizedPath("/dashboard/create-workflow/faceless-video", locale)}?intent=faceless-video&source=${encodeURIComponent(source)}`
      : getLocalizedPath("/dashboard", locale));

  // 检查用户是否已经登录，如果已登录则跳转到 dashboard
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        router.replace(destinationPath);
      }
    };

    checkUser();
  }, [supabase, router, destinationPath]);

  const resetTurnstile = useCallback(() => {
    setCaptchaToken(null);
    setTurnstileMessage(null);
    setTurnstileResetKey((current) => current + 1);
  }, []);

  const handleTurnstileStatusChange = useCallback(([status, errorCode]: [TurnstileStatus, string?]) => {
    if (status === "ready" || status === "verified") {
      setTurnstileMessage(null);
      return;
    }

    setTurnstileMessage(getTurnstileStatusMessage(status, errorCode, locale));
  }, [locale]);

  // Google OAuth 登录
  const handleOAuthLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOAuthLoading(true);
    setIsLoading(true);
    try {
      if (!isTurnstileConfigured) {
        toast.error("Cloudflare Turnstile is not configured");
        return;
      }

      if (!captchaToken) {
        toast.error(turnstileMessage || "Please complete the verification challenge");
        return;
      }

      const callbackUrl = new URL("/api/auth/callback", window.location.origin);
      callbackUrl.searchParams.set("next", destinationPath);
      const redirectURL = callbackUrl.toString();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectURL,
          captchaToken,
        } as any,
      });
      if (error) {
        toast.error(getAuthErrorMessage(error.message, (error as any).code, locale));
        resetTurnstile();
      }
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error?.message, error?.code, locale));
      resetTurnstile();
    } finally {
      setIsOAuthLoading(false);
      setIsLoading(false);
    }
  };

  // 邮箱+密码登录
  const handleEmailPasswordLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPasswordLoading(true);
    setIsLoading(true);
    try {
      if (!isTurnstileConfigured) {
        toast.error("Cloudflare Turnstile is not configured");
        return;
      }

      if (!captchaToken) {
        toast.error(turnstileMessage || "Please complete the verification challenge");
        return;
      }

      const normalizedEmail = email.trim().toLowerCase();
      const { error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
        options: {
          captchaToken,
        } as any,
      });
      if (error) {
        toast.error(getAuthErrorMessage(error.message, (error as any).code, locale));
        resetTurnstile();
      } else {
        toast.success(t("Sign in"));
        router.replace(destinationPath);
        router.refresh();
      }
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error?.message, error?.code, locale));
      resetTurnstile();
    } finally {
      setIsPasswordLoading(false);
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-64px)] pt-[64px] flex flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold tracking-tight">
            {t("Sign in")}
          </CardTitle>
          <CardDescription>{config.appName}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Google 登录 */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleOAuthLogin}
            disabled={isLoading || isOAuthLoading || !isTurnstileConfigured || !captchaToken}
          >
            {isOAuthLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-5 w-5"
                viewBox="0 0 48 48"
              >
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
            )}
            {t("Continue with Google")}
          </Button>

          {/* 分割线 */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                {t("OR")}
              </span>
            </div>
          </div>

          {/* 邮箱+密码登录表单 */}
          <form className="space-y-4" onSubmit={handleEmailPasswordLogin}>
            <div className="space-y-2">
              <Label htmlFor="login-email">{t("Email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="login-email"
                  required
                  type="email"
                  value={email}
                  autoComplete="email"
                  placeholder={t("Enter your email")}
                  className="pl-10"
                  onChange={(e) => setEmail(e.target.value.trimStart())}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">{t("Password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="login-password"
                  required
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  placeholder={t("Enter your password")}
                  className="pl-10"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isPasswordLoading || !isTurnstileConfigured || !captchaToken}
            >
              {isPasswordLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("Sign in")}
            </Button>
          </form>

          <div className="space-y-2">
            <CloudflareTurnstile
              siteKey={turnstileSiteKey}
              onTokenChange={setCaptchaToken}
              onStatusChange={handleTurnstileStatusChange}
              resetKey={turnstileResetKey}
            />
            {turnstileMessage && (
              <p className="text-sm text-error">
                {turnstileMessage}
              </p>
            )}
            {!isTurnstileConfigured && (
              <p className="text-sm text-error">
                Cloudflare Turnstile is not configured. Set `NEXT_PUBLIC_TURNSTILE_SITE_KEY`.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      <div className="mt-6 text-center text-sm text-muted-foreground">
        {t("Don't have an account yet?", { defaultValue: "Don't have an account yet?" })} {" "}
        <Link
          href={`${getLocalizedPath("/signup", locale)}${
            safeNext
              ? `?next=${encodeURIComponent(safeNext)}&source=${encodeURIComponent(source)}`
              : intent
                ? `?intent=${intent}&source=${encodeURIComponent(source)}`
                : ""
          }`}
          className="font-medium text-primary hover:underline"
        >
          {t("Sign up", { defaultValue: "Sign up" })}
        </Link>
      </div>
    </main>
  );
}
